import React, { useEffect, useRef, useState } from "react";
import "./BmsShowCase.css";

/* ── Right column features — always shown regardless of BMS type ── */
const RIGHT_FEATURES = [
  {
    id: "temp",
    icon: "🌡️",
    title: "Dual Temperature Sensors",
    desc: "Two NTC probes monitor cell and PCB temperature independently — cuts power before any damage.",
  },
  {
    id: "coat",
    icon: "💧",
    title: "Conformal Silicone Coating",
    desc: "PCB fully coated for humidity and dust resistance. Survives real-world installation environments.",
  },
  {
    id: "heatsink",
    icon: "🔲",
    title: "Thick Aluminum Heat Sink",
    desc: "Precision-milled heat sink dissipates high-current discharge heat without throttling.",
  },
];

/* ── Left column base — always shown ── */
const BASE_LEFT_FEATURES = [
  {
    id: "balance",
    icon: "⚖️",
    title: "Active Cell Balancing",
    desc: "Redistributes charge between cells in real time — maximises usable capacity and pack longevity.",
  },
  {
    id: "mosfet",
    icon: "🔩",
    title: "Low-Resistance MOSFETs",
    desc: "High-quality transistors with low internal resistance. Less heat, more power delivered to load.",
  },
];

/* ── Bluetooth card — injected ONLY when is_smart_bms === true ── */
const BLUETOOTH_FEATURE = {
  id: "bt",
  icon: "📱",
  title: "Bluetooth Monitoring",
  desc: "Live cell voltages, pack SOC, temperature, and fault history on your phone via dedicated app.",
};

/* ── Protection parameter cycling ticker ── */
const PROTECTION_PARAMS = [
  { label: "Overcharge Cutoff", key: "overcharge_detect_v" },
  { label: "Over-Discharge Cutoff", key: "over_discharge_detect_v" },
  { label: "Over Current", key: "over_current_detect_a" },
  { label: "Short Circuit Delay", key: "short_circuit_delay_us" },
  { label: "High Temp Cutoff", key: "high_temp_cutoff_c" },
  { label: "Low Temp Charge Off", key: "low_temp_charge_cutoff_c" },
];

const BMSShowcase = ({ productImage, specs }) => {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeParam, setActiveParam] = useState(0);

  /* Entrance observer */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 },
    );
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  /* Parallax on mouse move */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const dx = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const dy = (e.clientY - rect.top - rect.height / 2) / rect.height;
      const img = el.querySelector(".bms-product-img");
      const glow = el.querySelector(".bms-core-glow");
      if (img)
        img.style.transform = `translate(${dx * 10}px, ${dy * 8}px) scale(1.02)`;
      if (glow) glow.style.transform = `translate(${dx * 18}px, ${dy * 12}px)`;
    };
    const onLeave = () => {
      const img = el.querySelector(".bms-product-img");
      const glow = el.querySelector(".bms-core-glow");
      if (img) img.style.transform = "";
      if (glow) glow.style.transform = "";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  /* Cycle through protection params */
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(
      () => setActiveParam((p) => (p + 1) % PROTECTION_PARAMS.length),
      2000,
    );
    return () => clearInterval(t);
  }, [visible]);

  /* ── Extract values from spec JSON ── */
  const bmsSpecs = specs?.bms_specifications || {};
  const protection = specs?.protection_parameters || {};
  const balancing = specs?.balancing_specifications || {};
  const connectivity = specs?.connectivity_and_sensors || {};

  /*
   * SMART BMS DETECTION — two-tier:
   *
   * Tier 1 (explicit):  bms_specifications.is_smart_bms = true | false
   *   → This is the canonical field. Always set it in the JSON.
   *
   * Tier 2 (fallback):  if is_smart_bms is not present, infer from
   *   connectivity.bluetooth_module containing the word "included".
   *   This handles legacy products added before the field existed.
   *
   * What `isSmartBms` gates:
   *   ✓ "Bluetooth Monitoring" feature card (left column)
   *   ✓ "Bluetooth Connected" floating badge on the product image
   *   ✓ Eyebrow label ("SMART BMS" vs "BATTERY MANAGEMENT SYSTEM")
   */
  const isSmartBms =
    bmsSpecs.is_smart_bms === true ||
    (bmsSpecs.is_smart_bms === undefined &&
      connectivity.bluetooth_module?.toLowerCase().includes("included"));

  /* Left features list — bluetooth card added only for smart BMS */
  const leftFeatures = isSmartBms
    ? [...BASE_LEFT_FEATURES, BLUETOOTH_FEATURE]
    : BASE_LEFT_FEATURES;

  /* Spec strip values */
  const maxContinuous = bmsSpecs.max_continuous_discharge_a
    ? `${bmsSpecs.max_continuous_discharge_a}A`
    : "—";
  const peakA = bmsSpecs.peak_discharge_current_a || "—";
  const series = bmsSpecs.series_configuration || "—";
  const chemistry = bmsSpecs.supported_chemistry || "LiFePO4";
  const balType = balancing.balancing_type || "Active";

  const imgSrc =
    productImage || "https://placehold.co/500x400/080808/222?text=BMS+Board";

  const currentParam = PROTECTION_PARAMS[activeParam];
  const currentVal = protection[currentParam.key] || "—";

  return (
    <section
      className={`bms-root${visible ? " bms-visible" : ""}`}
      ref={rootRef}
    >
      {/* Background */}
      <div className="bms-bg">
        <div className="bms-bg-circuit" />
        <div className="bms-bg-vignette" />
      </div>

      {/* Eyebrow — reflects smart vs standard */}
      <div className="bms-eyebrow">
        <span className="bms-eyebrow-dot" />
        {isSmartBms
          ? "SMART BATTERY MANAGEMENT SYSTEM"
          : "BATTERY MANAGEMENT SYSTEM"}
      </div>

      {/* Headline */}
      <h2 className="bms-headline">
        The brain
        <br />
        <em>of every great pack.</em>
      </h2>

      {/* Three-column stage */}
      <div className="bms-stage">
        {/* LEFT — 2 base features always, +bluetooth only if smart */}
        <div className="bms-col bms-col-left">
          {leftFeatures.map((f, i) => (
            <BmsFeatureCard
              key={f.id}
              feature={f}
              delay={i * 100}
              align="right"
              active={activeFeature === f.id}
              onHover={setActiveFeature}
            />
          ))}
        </div>

        {/* CENTER */}
        <div className="bms-center">
          <div className="bms-core-glow" />
          <div className="bms-ring bms-ring-1" />

          {/* Protection parameter ticker — fixed-size box, never causes reflow */}
          <div className="bms-live-display">
            <div className="bms-display-scan" />
            <div className="bms-display-inner">
              <span className="bms-display-label">{currentParam.label}</span>
              <span className="bms-display-value" key={activeParam}>
                {currentVal}
              </span>
              <div className="bms-display-dots">
                {PROTECTION_PARAMS.map((_, i) => (
                  <span
                    key={i}
                    className={`bms-disp-dot${activeParam === i ? " bms-disp-dot--on" : ""}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <img
            src={imgSrc}
            alt="Zynvert BMS Board"
            className="bms-product-img"
            onError={(e) =>
              (e.target.src =
                "https://placehold.co/400x350/080808/333?text=BMS")
            }
          />

          {/* Bluetooth badge — ONLY for smart BMS */}
          {isSmartBms && (
            <div className="bms-badge bms-badge-bt">
              <span className="bms-badge-dot" />
              <span className="bms-badge-text">Bluetooth Connected</span>
            </div>
          )}

          {/* Series / chemistry badge — always shown */}
          <div className="bms-badge bms-badge-series">
            <span className="bms-badge-text-plain">
              {series} · {chemistry}
            </span>
          </div>
        </div>

        {/* RIGHT — always same 3 hardware features */}
        <div className="bms-col bms-col-right">
          {RIGHT_FEATURES.map((f, i) => (
            <BmsFeatureCard
              key={f.id}
              feature={f}
              delay={i * 100 + 150}
              align="left"
              active={activeFeature === f.id}
              onHover={setActiveFeature}
            />
          ))}
        </div>
      </div>

      {/* Spec strip */}
      <div className="bms-spec-strip">
        {[
          [maxContinuous, "Continuous Discharge"],
          [peakA, "Peak Discharge"],
          [series, "Cell Configuration"],
          [balType, "Balancing Type"],
          [chemistry, "Chemistry"],
        ].map(([val, label]) => (
          <div className="bms-spec" key={label}>
            <span className="bms-spec-val">{String(val)}</span>
            <span className="bms-spec-label">{label}</span>
          </div>
        ))}
      </div>

      <p className="bms-footnote">
        * Protection thresholds are factory-calibrated. Do not modify. Follow
        wiring diagram strictly.
      </p>
    </section>
  );
};

const BmsFeatureCard = ({ feature, delay, align, active, onHover }) => (
  <div
    className={`bms-feat bms-feat-${align}${active ? " bms-feat-active" : ""}`}
    style={{ transitionDelay: `${delay}ms` }}
    onMouseEnter={() => onHover(feature.id)}
    onMouseLeave={() => onHover(null)}
  >
    <div className="bms-feat-icon-wrap">
      <span className="bms-feat-icon">{feature.icon}</span>
    </div>
    <div className="bms-feat-body">
      <p className="bms-feat-title">{feature.title}</p>
      <p className="bms-feat-desc">{feature.desc}</p>
    </div>
  </div>
);

export default BMSShowcase;
